import { Button, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { getTags } from 'api/tags'
import { MIN_SEARCH_VALUE_LENGTH } from 'const'
import React from 'react'
import { useQuery } from 'react-query'
import { EOp, FilterModel } from 'types'
import { selectOptionsFromTableResponse } from 'utils'
import { useShowTotalEngagement } from '../../features/Metric/hooks/useShowTotalEngagement'
import { useDrawersContext } from '../context/ClientsDrawersProvider'
import NotificationSys from '../NotificationSystem'
import HelpTooltip, { MobileTooltipWithIcon } from '../tooltips/HelpTooltip'
import { addNoteTooltip, searchTooltip } from '../tooltips/tooltips'
import { useSearchInput } from './useSearchInput'

const getTagFilterById = (id: number): FilterModel => ({
  tagId: {
    type: 'number',
    op: EOp.equals,
    value: id,
  },
})

const FooterContent = () => {
  const theme = useTheme()
  const { handleOpenNoteEdit, handleOpenSearchDrawer } = useDrawersContext()
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'))

  const { totalTimeText } = useShowTotalEngagement()

  const tagsQuery = useQuery(['getTags'], () => getTags(), {
    select: selectOptionsFromTableResponse,
  })

  const { SearchInput, searchInput } = useSearchInput({
    options: tagsQuery.data,
    isLoading: tagsQuery.isLoading,
    getFilters: getTagFilterById,
    onSearch: handleOpenSearchDrawer,
  })

  const onSearch = () => {
    if (searchInput.trim().length < MIN_SEARCH_VALUE_LENGTH) {
      NotificationSys.showWarning('Please enter 3 symbols or more to be able to search.')
      return
    }

    handleOpenSearchDrawer(searchInput, null)
  }

  const onClickAddNote = () => {
    handleOpenNoteEdit()
  }

  return (
    <Stack
      spacing={1}
      direction={matchesXs ? 'column' : 'row'}
      alignItems="center"
      justifyContent="space-between"
      flexGrow={1}
      padding={matchesXs ? '8px 0' : 0}
    >
      <Stack
        spacing={matchesXs ? 1 : 2}
        direction={matchesXs ? 'column' : 'row'}
        alignItems={matchesXs ? 'stretch' : 'center'}
        width={matchesXs ? '100%' : 'auto'}
      >
        {SearchInput}

        <Stack direction="row" spacing={1} width={matchesXs ? '100%' : 'auto'}>
          <HelpTooltip text={searchTooltip} placement="top">
            <Button
              variant="contained"
              onClick={onSearch}
              fullWidth={matchesXs}
              sx={{ flex: matchesXs ? 1 : 'none' }}
            >
              Search
              <MobileTooltipWithIcon text={searchTooltip} placement="top" />
            </Button>
          </HelpTooltip>
          <HelpTooltip text={addNoteTooltip} placement="top">
            <Button
              variant="contained"
              color="secondary"
              onClick={onClickAddNote}
              fullWidth={matchesXs}
              sx={{ flex: matchesXs ? 1 : 'none' }}
            >
              Add note
              <MobileTooltipWithIcon text={addNoteTooltip} placement="top" />
            </Button>
          </HelpTooltip>
        </Stack>
      </Stack>

      {totalTimeText && (
        <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
          <Typography variant="body2" color="textSecondary">
            Total Time Online:{'\u00A0'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {totalTimeText}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default React.memo(FooterContent)
